import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Copy, Network, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";

import {
  decideGrowthPayout, enrollGrowthPartner, getGrowthDashboard, getGrowthFounderQueue,
  growthSignupLink, requestGrowthPayout, setGrowthPartnerStatus,
} from "@/entities/growth-network";
import { useFounderMode } from "@/entities/auth/useFounderMode";
import { useCurrentWorkspace } from "@/entities/workspace";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";

const money = (cents: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
const rate = (bps: number) => `${bps / 100}%`;

export default function GrowthNetworkPage(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const founderMode = useFounderMode();
  const qc = useQueryClient();
  const ws = workspace?.id ?? "";
  const q = useQuery({ queryKey: ["growth-network", ws], queryFn: () => getGrowthDashboard(ws), enabled: Boolean(ws) });
  const founderQueue = useQuery({
    queryKey: ["growth-founder-queue", ws], queryFn: () => getGrowthFounderQueue(ws),
    enabled: Boolean(ws && founderMode),
  });
  const refreshGrowth = () => {
    qc.invalidateQueries({ queryKey: ["growth-network", ws] });
    qc.invalidateQueries({ queryKey: ["growth-founder-queue", ws] });
  };
  const enroll = useMutation({
    mutationFn: () => enrollGrowthPartner(ws),
    onSuccess: () => { toast.success("Growth Network enrollment created."); qc.invalidateQueries({ queryKey: ["growth-network", ws] }); },
    onError: (e: Error) => toast.error(e.message),
  });
  const payout = useMutation({
    mutationFn: () => requestGrowthPayout(ws),
    onSuccess: () => { toast.success("Payout request reserved for founder review."); qc.invalidateQueries({ queryKey: ["growth-network", ws] }); },
    onError: (e: Error) => toast.error(e.message),
  });
  const partnerDecision = useMutation({
    mutationFn: ({ partnerId, status }: { partnerId: string; status: "active" | "suspended" }) =>
      setGrowthPartnerStatus(ws, partnerId, status),
    onSuccess: (_data, variables) => { toast.success(variables.status === "active" ? "Partner approved." : "Partner suspended from future eligibility."); refreshGrowth(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const payoutDecision = useMutation({
    mutationFn: ({ requestId, decision }: { requestId: string; decision: "approved" | "rejected" }) =>
      decideGrowthPayout(ws, requestId, decision, decision === "rejected" ? "Rejected by founder review." : undefined),
    onSuccess: (_data, variables) => { toast.success(variables.decision === "approved" ? "Request approved in the internal ledger. No money was transferred." : "Request rejected and reserved events released."); refreshGrowth(); },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!workspace) return <Empty message="Select a workspace to open Growth Network." />;
  if (q.isLoading) return <Empty message="Loading Growth Network truth…" />;
  if (q.isError || !q.data) return <Empty message="Growth Network truth is temporarily unavailable." />;
  const data = q.data;
  const referralActive = data.partner?.status === "active";

  return (
    <main className="mx-auto max-w-[1120px] space-y-6 px-4 py-8 md:px-8 md:py-12" data-testid="growth-network-page">
      <header>
        <div className="md-eyebrow">Orkexra · Growth Network</div>
        <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight sm:text-5xl">Build customers, not a downline.</h1>
        <p className="mt-3 max-w-3xl text-sm text-[var(--md-fog)]">Share Orkexra with businesses you genuinely help. Attribution is immutable, commissions are governed, and the network stops at two levels.</p>
      </header>

      {founderMode && (
        <FounderAdministration
          queue={founderQueue.data}
          loading={founderQueue.isLoading}
          unavailable={founderQueue.isError}
          deciding={partnerDecision.isPending || payoutDecision.isPending}
          onPartnerDecision={(partnerId, status) => partnerDecision.mutate({ partnerId, status })}
          onPayoutDecision={(requestId, decision) => payoutDecision.mutate({ requestId, decision })}
        />
      )}

      {!data.enrolled ? (
        <Card data-testid="growth-enrollment" className="border-[color:var(--md-line)] bg-[var(--md-ink-1)]/60">
          <CardHeader><CardTitle>Join the Growth Network</CardTitle></CardHeader>
          <CardContent className="space-y-4 text-sm text-[var(--md-fog)]">
            <p>Enrollment creates your unique referral identity. New partners begin pending and cannot earn until approved.</p>
            <Button onClick={() => enroll.mutate()} disabled={enroll.isPending}>Create my referral identity</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <section className="grid gap-4 md:grid-cols-3">
            <Metric icon={ShieldCheck} label="Partner status" value={data.partner?.status ?? "—"} />
            <Metric icon={Users} label="Direct customers" value={String(data.network.direct_customers)} />
            <Metric icon={Network} label="Second-level customers" value={String(data.network.second_level_customers)} />
          </section>
          <Card className="border-[color:var(--md-line)] bg-[var(--md-ink-1)]/60">
            <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
              <div><p className="md-eyebrow">Your customer signup link</p><p className="mt-1 break-all font-mono text-sm text-[var(--md-ivory-0)]" data-testid="referral-link">{referralActive && data.partner ? growthSignupLink(data.partner.referral_code) : "Activates after founder approval"}</p><p className="mt-1 text-xs text-[var(--md-fog)]">Referral code: <span data-testid="referral-code">{data.partner?.referral_code}</span></p></div>
              <Button variant="outline" disabled={!referralActive} onClick={() => { navigator.clipboard.writeText(data.partner ? growthSignupLink(data.partner.referral_code) : ""); toast.success("Customer signup link copied."); }}><Copy className="mr-2 h-4 w-4" />Copy signup link</Button>
            </CardContent>
          </Card>
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="growth-balances">
            {(["pending", "matured", "paid", "reversed"] as const).map((key) => <Balance key={key} label={key} cents={data.balances[key]} />)}
          </section>
          {data.payout && (
            <Card data-testid="growth-payout-readiness" className="border-[color:var(--md-line)] bg-[var(--md-ink-1)]/60">
              <CardHeader><CardTitle>Payout readiness</CardTitle></CardHeader>
              <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div className="space-y-1 text-sm text-[var(--md-fog)]">
                  <p><span className="font-mono text-xl text-[var(--md-ivory-0)]">{money(data.payout.available_cents)}</span> available</p>
                  <p>{data.payout.eligible ? "Minimum met and no request is open." : payoutReason(data.payout.reasons)}</p>
                  <p className="text-xs">Requests enter founder review. Approval here does not transfer money; Stripe Connect remains deferred.</p>
                </div>
                <Button onClick={() => payout.mutate()} disabled={!data.payout.eligible || payout.isPending}>Request payout</Button>
              </CardContent>
            </Card>
          )}
          <section className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
            <Card className="border-[color:var(--md-line)] bg-[var(--md-ink-1)]/60">
              <CardHeader><CardTitle>How earnings work</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm text-[var(--md-fog)]">
                <Rule text={`Direct customer: ${rate(data.policy.l1_rate_bps)} lifetime commission.`} />
                <Rule text={`Second level: ${rate(data.policy.l2_rate_bps)} for the first ${data.policy.l2_window_months} months.`} />
                <Rule text={`${data.policy.hold_days}-day hold before earnings mature.`} />
                <Rule text={`${money(data.policy.minimum_payout_cents)} minimum before a payout can be requested.`} />
                <p className="pt-2 text-xs">No third level. No commission for self-referrals. Historical events remain immutable.</p>
              </CardContent>
            </Card>
            <Card className="border-[color:var(--md-line)] bg-[var(--md-ink-1)]/60">
              <CardHeader><CardTitle>Recent ledger activity</CardTitle></CardHeader>
              <CardContent>
                {data.recent_activity.length === 0 ? <p className="text-sm text-[var(--md-fog)]">No commission activity yet. Future eligible billing events will appear here.</p> : (
                  <div className="space-y-2">{data.recent_activity.map((event) => (
                    <div key={event._id} className="flex items-center justify-between gap-3 rounded-lg border border-[color:var(--md-line)] px-3 py-3 text-sm">
                      <div><p className="capitalize text-[var(--md-ivory-0)]">{event.event_type} · Level {event.level}</p><p className="text-xs capitalize text-[var(--md-fog)]">{event.status.replaceAll("_", " ")}</p></div>
                      <p className="font-mono">{money(event.amount_cents)}</p>
                    </div>
                  ))}</div>
                )}
              </CardContent>
            </Card>
          </section>
        </>
      )}
    </main>
  );
}

function FounderAdministration({ queue, loading, unavailable, deciding, onPartnerDecision, onPayoutDecision }: {
  queue: Awaited<ReturnType<typeof getGrowthFounderQueue>> | undefined;
  loading: boolean; unavailable: boolean; deciding: boolean;
  onPartnerDecision: (partnerId: string, status: "active" | "suspended") => void;
  onPayoutDecision: (requestId: string, decision: "approved" | "rejected") => void;
}): JSX.Element {
  return (
    <Card data-testid="growth-founder-administration" className="border-amber-500/40 bg-amber-500/5">
      <CardHeader>
        <CardTitle>Founder administration</CardTitle>
        <p className="text-sm text-[var(--md-fog)]">Govern partner access, review integrity evidence, and decide internal payout reservations.</p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="rounded-lg border border-amber-500/30 bg-black/20 p-3 text-xs text-[var(--md-fog)]">
          No transfer is executed here. Stripe Connect and live money movement remain founder-deferred.
        </div>
        {loading && <p className="text-sm text-[var(--md-fog)]">Loading founder review queue…</p>}
        {unavailable && <p className="text-sm text-red-300">Founder review truth is temporarily unavailable. Refresh before making a decision.</p>}
        {queue && (
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-2 text-center text-xs sm:text-sm">
              <QueueCount label="Pending partners" value={queue.counts.pending_partners} />
              <QueueCount label="Payout requests" value={queue.counts.requested_payouts} />
              <QueueCount label="Integrity flags" value={queue.counts.flagged_partners} />
            </div>
            <FounderSection title="Partner approvals" empty="No partners are awaiting approval.">
              {queue.pending_partners.map((partner) => (
                <div key={partner._id} className="rounded-lg border border-[color:var(--md-line)] p-4">
                  <p className="font-mono text-sm">{partner.referral_code}</p>
                  <p className="mt-1 text-xs text-[var(--md-fog)]">Enrolled {new Date(partner.created_at).toLocaleDateString()}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" disabled={deciding} onClick={() => onPartnerDecision(partner._id, "active")}>Approve partner</Button>
                    <Button size="sm" variant="outline" disabled={deciding} onClick={() => onPartnerDecision(partner._id, "suspended")}>Hold for review</Button>
                  </div>
                </div>
              ))}
            </FounderSection>
            <FounderSection title="Payout reservations" empty="No payout requests are awaiting a decision.">
              {queue.requested_payouts.map((request) => (
                <div key={request._id} className="rounded-lg border border-[color:var(--md-line)] p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2"><p className="font-mono text-lg">{money(request.amount_cents)}</p><span className="text-xs uppercase tracking-wider text-[var(--md-fog)]">requested</span></div>
                  <p className="mt-1 break-all text-xs text-[var(--md-fog)]">Partner {request.partner_id}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" disabled={deciding} onClick={() => onPayoutDecision(request._id, "approved")}>Approve request</Button>
                    <Button size="sm" variant="outline" disabled={deciding} onClick={() => onPayoutDecision(request._id, "rejected")}>Reject and release</Button>
                  </div>
                </div>
              ))}
            </FounderSection>
            <FounderSection title="Integrity evidence" empty="No partner currently requires integrity review.">
              {queue.flagged_partners.map(({ partner, integrity }) => (
                <div key={partner._id} className="rounded-lg border border-red-500/30 bg-red-500/5 p-4">
                  <div className="flex flex-wrap items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-400" /><p className="font-mono text-sm">{partner.referral_code}</p><span className="rounded-full border border-red-500/30 px-2 py-0.5 text-xs uppercase text-red-200">{integrity.risk_level}</span></div>
                  <div className="mt-3 space-y-2">{integrity.signals.map((signal) => (
                    <div key={signal.code} className="rounded border border-[color:var(--md-line)] p-3 text-sm">
                      <p className="font-medium">{signal.detail}</p>
                      <p className="mt-1 text-xs uppercase tracking-wide text-[var(--md-fog)]">{signal.severity} · {signal.code.replaceAll("_", " ")}</p>
                      <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words text-xs text-[var(--md-fog)]">{JSON.stringify(signal.evidence, null, 2)}</pre>
                    </div>
                  ))}</div>
                  <p className="mt-3 text-xs text-[var(--md-fog)]">Signals are advisory. No automatic action was taken.</p>
                  {partner.status === "active" && <Button className="mt-3" size="sm" variant="outline" disabled={deciding} onClick={() => onPartnerDecision(partner._id, "suspended")}>Suspend future eligibility</Button>}
                </div>
              ))}
            </FounderSection>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function QueueCount({ label, value }: { label: string; value: number }): JSX.Element { return <div className="rounded-lg border border-[color:var(--md-line)] p-3"><p className="text-xl font-semibold">{value}</p><p className="mt-1 text-[var(--md-fog)]">{label}</p></div>; }
function FounderSection({ title, empty, children }: { title: string; empty: string; children: React.ReactNode }): JSX.Element { const rows = Array.isArray(children) ? children : [children]; return <section><h3 className="mb-2 font-semibold">{title}</h3>{rows.length === 0 ? <p className="text-sm text-[var(--md-fog)]">{empty}</p> : <div className="grid gap-3 md:grid-cols-2">{children}</div>}</section>; }

function Metric({ icon: Icon, label, value }: { icon: typeof Users; label: string; value: string }): JSX.Element { return <Card className="border-[color:var(--md-line)] bg-[var(--md-ink-1)]/60"><CardContent className="p-5"><Icon className="h-5 w-5 text-[var(--md-amber)]" /><p className="mt-4 text-xs uppercase tracking-widest text-[var(--md-fog)]">{label}</p><p className="mt-1 text-2xl font-semibold capitalize">{value}</p></CardContent></Card>; }
function Balance({ label, cents }: { label: string; cents: number }): JSX.Element { return <Card className="border-[color:var(--md-line)] bg-[var(--md-ink-1)]/60"><CardContent className="p-4"><p className="text-xs uppercase tracking-widest text-[var(--md-fog)]">{label}</p><p className="mt-2 font-mono text-xl">{money(cents)}</p></CardContent></Card>; }
function Rule({ text }: { text: string }): JSX.Element { return <p className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />{text}</p>; }
function Empty({ message }: { message: string }): JSX.Element { return <div className="mx-auto max-w-2xl px-6 py-16 text-center text-[var(--md-fog)]">{message}</div>; }
function payoutReason(reasons: string[]): string {
  if (reasons.includes("open_request_exists")) return "A payout request is already awaiting review.";
  if (reasons.includes("cooldown_active")) return "The 24-hour request cooldown is active.";
  if (reasons.includes("partner_not_active")) return "Partner approval is required before requesting payout.";
  return "The $20 minimum has not been reached yet.";
}
